import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetOperation,
  setCommandMessage,
  setCommandType,
  setOperationData,
} from '../../../redux/operationSlice';
import { clearSelection } from '../../../redux/selectionSlice';
import { COMMANDS } from '../../../utils/commonProperties';
import { toggleSnapMode, toggleOrthoMode } from '../../../redux/modsSlice';
import { createPanel } from '../../../factory/symbols/createPanel';
import { useComponentWiring } from '../../../modules/wiring';
import useSnapPoints from '../../../shared/snap/useSnapPoints';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { PanelCommand } from '../../../modules/undoRedo';
import parseColor from '../../../utils/parseColor';
import { ComponentFactory } from '../../../factories/ComponentFactory';
import { FeederFactory } from '../../../factories/FeederFactory';
import { addWire } from '../../../redux/wireSlice';
import { selectAllFloorPlans } from '../../../redux/floorPlanSlice';
import { buildDefaultPanelName } from '../../../utils/panelNaming';

export const useElectricalPanel = (scene, camera, renderer, undoRedoManagerRef) => {
  const dispatch = useDispatch();
  const { commandType } = useSelector((s) => s.operation);
  const { snapPoints } = useSnapPoints(scene);
  const settings = useSelector((state) => state.settings);
  const dynamicSnapPoints = useRef([]);
  const wiringActiveRef = useRef(false);
  const sceneRef = useRef(null);
  const operation = useSelector((s) => s.operation);
  const snapMode = useSelector((s) => s.mods.snapMode);
  const orthoMode = useSelector((s) => s.mods.orthoMode);
  const floorPlans = useSelector(selectAllFloorPlans);
  const supplyComponents = useSelector((s) => s.simpleSupply.components);

  // ✅ Panel wire color
  const parsedWireColor = parseColor(settings?.panel?.wireColor ?? 0x2233ff);
  const wireColor = parsedWireColor.colorNumber;

  // ✅ Panel Configuration
  const panelConfig = {
    commandType: COMMANDS.DRAW_PANEL,
    itemTypeName: 'panel',
    wireColor: wireColor,
    allowSpaceKey: false, // ✅ Panel'de Space ile ekleme yok, sadece Enter
    skipItemExecution: true, // ✅ addItem()'da execute atlanır, handleWireEnd'de yapılır
    markerCount: 0, // ✅ Panel hatlarında wireMarker oluşturma
    createSymbol: (position, direction, angle, isPreview, itemType, extraData = {}) => {
      const colorSetting = isPreview
        ? (settings?.panel?.previewColor ?? settings?.panel?.color ?? wireColor)
        : (settings?.panel?.color ?? wireColor);
      const parsed = parseColor(colorSetting);
      // ✅ Preview'da da panel position wire direction'ına göre 7.5 birim ötede olmalı
      const normalizedDir = direction.clone().normalize();
      const panelPreviewPos = position.clone().add(normalizedDir.multiplyScalar(7.5));
      // Auto-name: operation.data.source.name (üst akıştan geldiyse) veya
      // panelPreviewPos'un katına göre buildDefaultPanelName ile üret. Final
      // çağrıda boş kalırsa createPanel destructure default'u "Panel" döner.
      const sourceName = operation.data?.source?.name || operation.data?.panelName;
      const autoName = buildDefaultPanelName({
        position: panelPreviewPos,
        floorPlans,
        allComponents: supplyComponents,
      });
      const panelInfo = {
        name: (sourceName && sourceName.trim()) || autoName || 'Panel',
        color: parsed.colorNumber,
        purposeOfUse: operation.data?.source?.purposeOfUse || 'Mesken',
        aboneTuru: operation.data?.source?.aboneTuru || 'Mesken',
      };
      const panel = createPanel(null, panelPreviewPos, 1, 0, isPreview, 0, panelInfo);
      panel.rotation.z = angle;
      return panel;
    },
    // ❌ createCommand KALDIRILDI - Panel için command sadece handleWireEnd içinde oluşturulur
    createCommand: null,
    // ❌ createFeederData null - Panel için feeder oluşturma yok (handleWireEnd'de yapılır)
    createFeederData: null,
    // ❌ createComponentData null - Panel için component oluşturma yok (handleWireEnd'de yapılır)
    createComponentData: null,
    itemTypes: [
      { name: 'Panel', type: 'panel', power: 0 }
    ],
    onKeyDown: null
  };

  useEffect(() => {
    if (commandType !== COMMANDS.DRAW_PANEL || !scene || !camera || !renderer) return;

    dispatch(setCommandMessage('Panel yerleştirme için hatta tıklayın'));
    if (!snapMode) dispatch(toggleSnapMode());
    if (!orthoMode) dispatch(toggleOrthoMode());

    sceneRef.current = scene;

    // sadece panel çıkış noktalarını al
    dynamicSnapPoints.current = [];
    scene.traverse((obj) => {
      if (obj.userData?.connectionPoints) {
        obj.userData.connectionPoints.forEach((p) => {
          if (p.type === 'panelCon_out') {
            const worldPos = p.position.clone().applyMatrix4(obj.matrixWorld);
            dynamicSnapPoints.current.push({
              no: obj.uuid,
              position: worldPos,
              type: 'panelConnection',
            });
          }
        });
      }
    });

    wiringActiveRef.current = true;

    // ✅ Cleanup: commandType değiştiğinde wiring'i devre dışı bırak
    return () => {
      wiringActiveRef.current = false;
    };
  }, [commandType, scene, camera, renderer]);

  // ✅ Panel placement confirmation
  const confirmPlacement = (vertices, panels, source, panel, tempLineUUID, wireMarkers) => {
    if (!vertices || vertices.length < 2) {
      return;
    }

    // ✅ ID'ler
    const panelId = generateUniqueId('panel');
    const feederId = generateUniqueId('panelFeeder');
    const wireId = generateUniqueId('wirePanelFeeder');

    // ✅ Panel name (type menu'den gelen isim)
    const panelName = operation.data?.source?.name || operation.data?.panelName || 'Panel';

    // ✅ Panel direction ve position hesaplama (wire panelin KENARINDA bitmeli)
    const lastVertex = vertices[vertices.length - 1];
    const prevVertex = vertices[vertices.length - 2] || vertices[vertices.length - 1];
    const direction = new THREE.Vector3().subVectors(lastVertex, prevVertex).normalize();

    // ✅ Son kenarı EXACTLY 15 birim yap
    const newLastVertex = prevVertex.clone().add(direction.clone().multiplyScalar(15));
    vertices[vertices.length - 1] = newLastVertex;

    const panelAngle = Math.atan2(direction.y, direction.x);

    // Wire panelin kenarında bitmeli, panel merkezi wire sonundan 7.5 birim ötede olmalı
    const panelPosition = newLastVertex.clone().add(direction.clone().multiplyScalar(7.5));

    // ✅ Source panel ID
    const sourcePanelId = source?.p?.panelID || source?.componentId || source?.no;

    // ✅ Component (panel) oluştur
    const purposeOfUse = operation.data?.source?.purposeOfUse || operation.data?.purposeOfUse || 'Mesken';
    const aboneTuru = operation.data?.source?.aboneTuru || operation.data?.aboneTuru || 'Mesken';
    const panelComponent = ComponentFactory.createPanel('dist_panel', sourcePanelId, {
      id: panelId,
      name: panelName,
      electrical: {
        purposeOfUse: purposeOfUse,
        aboneTuru: aboneTuru,
      },
      position: { x: panelPosition.x, y: panelPosition.y, z: 0 },
      rotation: { x: 0, y: 0, z: panelAngle },
      metadata: {
        fromPanelId: sourcePanelId
      }
    });

    // ✅ Feeder oluştur
    const cableLength = vertices.reduce((sum, v, i, arr) => {
      if (i === 0) return 0;
      const prev = arr[i - 1];
      return sum + Math.hypot(v.x - prev.x, v.y - prev.y);
    }, 0);

    const panelFeeder = FeederFactory.createFeederForLoad(
      'panel',
      0,
      sourcePanelId,
      panelId,
      {
        id: feederId,
        vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
        cable: {
          type: 'NYM',
          crossSection: 4,
          length: Math.round(cableLength / 10)
        },
        electrical: {
          voltage: 220,
          current: 0
        },
        metadata: {
          wireId: wireId
        }
      }
    );

    // ✅ PanelCommand oluştur
    const panelCommand = new PanelCommand(
      'ADD',
      panelComponent,
      panelFeeder,
      {
        scene: scene,
        dispatch: dispatch,
        wireId: wireId,
        color: wireColor,
        sourcePanelId: sourcePanelId, // ✅ Source panel ID (connectedWires için)
        restoreVertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
        restoreSource: source ? {
          type: source?.type,
          p: source?.p,
          no: source?.no,
          componentId: source?.componentId,
          panelID: source?.p?.panelID,
          position: source?.position ? {
            x: source.position.x,
            y: source.position.y,
            z: source.position.z || 0
          } : null
        } : null,
        // 🆕 REDO mode desteği ile güncellenmiş restoreCallback
        restoreCallback: (restoreVertices, restoreSource, isRedoContext = false, sceneData = null) => {
 
          dispatch(setCommandType(COMMANDS.DRAW_PANEL));

          setTimeout(() => {
            const operationData = {
              initialPoint: restoreVertices && restoreVertices[0] ? restoreVertices[0] : null,
              source: restoreSource || {
                type: source?.type,
                p: source?.p,
                no: source?.no,
                componentId: source?.componentId,
                panelID: source?.p?.panelID,
                position: source?.position ? {
                  x: source.position.x,
                  y: source.position.y,
                  z: source.position.z || 0
                } : null
              },
              _restoreId: Date.now() + Math.random()
            };

            if (isRedoContext) {
              // REDO: Sadece ilk vertex, geri kalanlar vertexHistory'de
              operationData.restoreVertices = [restoreVertices[0]];
              operationData.vertexHistory = restoreVertices.slice(1);
              operationData.isRedo = true;
              // 🆕 onRedoComplete callback'ini aktar (chain advancement için)
              operationData.onRedoComplete = sceneData?.onRedoComplete;
             } else {
              // UNDO: Tüm vertices yükle
              operationData.restoreVertices = restoreVertices;
              operationData.isRedo = false;
             }

            dispatch(setOperationData(operationData));
          }, 16);
        }
      }
    );

    // ✅ Global Undo/Redo transaction
    if (undoRedoManagerRef?.current) {
      const tx = undoRedoManagerRef.current.beginTransaction('Add Electrical Panel');
      undoRedoManagerRef.current.addCommand(panelCommand);
      undoRedoManagerRef.current.commitTransaction(tx);
    }

    panelCommand.execute();

    // ✅ Wire'ı wireSlice'a ekle
    dispatch(addWire({
      id: wireId,
      type: 'polyline',
      vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
      layer: 'electricalPanel',
      color: '#' + wireColor.toString(16).padStart(6, '0'),
      userData: {
        selectable: true,
        parent: 'electricalPanel',
        componentId: panelId
      }
    }));

    dispatch(setCommandMessage('Panel yerleştirildi (Ctrl+Z ile geri alınabilir)'));

    // 🔍 DEBUG ESC: Panel placement complete
    dispatch(resetOperation());
    dispatch(clearSelection()); // ✅ ESC ile aynı temizlik
    dispatch(setCommandMessage('')); // ✅ Mesajı temizle
    wiringActiveRef.current = false;

    return { wireId: wireId, panelId: panelId };
  };

  // ✅ Panel wire end handler
  const handleWireEnd = (vertices, panels, source, panel, tempLineUUID, wireMarkers) => {
    return confirmPlacement(vertices, panels, source, panel, tempLineUUID, wireMarkers);
  };

  // ✅ useComponentWiring entegrasyonu
  useComponentWiring(
    scene,
    camera,
    renderer,
    [...snapPoints, ...dynamicSnapPoints.current],
    {
      ...panelConfig,
      enabled: () => wiringActiveRef.current,
      onWireEnd: handleWireEnd,
      onItemAdd: (item) => {
       },
      onPreviewUpdate: () => {},
      onWireCancel: () => {
        wiringActiveRef.current = false;
        dispatch(resetOperation());
        dispatch(clearSelection());
        dispatch(setCommandMessage(''));
      },
      undoRedoManagerRef: undoRedoManagerRef // ✅ Auto-finish için gerekli
    }
  );
};
